import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
gsap.registerPlugin(ScrollTrigger, SplitText);

import { FormValidator } from '@utils/forms.js';
import FlipButton from '../snippets/flipbutton';

export default class WholesaleForm {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('form');
    this.title = container.querySelector('.ws-form__title');

    this.animateTitle();
    this.animateGroupLabels();
    this.animateItems();

    if (!this.form) return;

    this.validator = new FormValidator(this.form, {
      ajax: true,
      successSelector: '.ws-form__notice--success',
      errorSelector: '.ws-form__notice--error',
    });
    this.validator.init();

    this.bindProductsChoices();
    this.initSubmitFlip();
    this.scrollToNoticeIfPresent();
  }

  initSubmitFlip() {
    const label = this.form.querySelector('.ws-form__submit .js-flipbutton');
    if (label) new FlipButton(label, { fade: false });
  }

  animateTitle() {
    if (!this.title) return;
    const textLines = new SplitText(this.title, { type: 'lines,words', linesClass: 'lineParent' });
    gsap.fromTo(
      textLines.words,
      { yPercent: 100 },
      {
        yPercent: 0,
        duration: 0.6,
        stagger: { each: 0.1, ease: 'power.out', from: 'start' },
        ease: 'power3.out',
        scrollTrigger: { trigger: this.title, start: 'top 85%' },
      }
    );
  }

  animateGroupLabels() {
    const labels = gsap.utils.toArray('.ws-form__group-label', this.container);
    if (!labels.length) return;
    gsap.set(labels, { opacity: 0, x: -40 });

    ScrollTrigger.batch(labels, {
      start: 'top 88%',
      onEnter: (els) =>
        gsap.to(els, { opacity: 1, x: 0, duration: 1, stagger: 0.1, ease: 'power2.out' }),
    });
  }

  animateItems() {
    const items = gsap.utils.toArray('.ws-form__subtitle, .ws-form__card, .ws-form__consent, .ws-form__submit', this.container);
    if (!items.length) return;
    gsap.set(items, { opacity: 0, y: 30 });
    ScrollTrigger.batch(items, {
      start: 'top 88%',
      onEnter: (els) =>
        gsap.to(
          els,
          { opacity: 1, y: 0, duration: 1, stagger: 0.1, ease: 'power2.out' }
        ),
    });
  }

  // "All of the above" is exclusive with the other choices, and every change
  // is mirrored into the single hidden contact[products] field, because
  // Shopify rejects array-valued contact fields (BugHerd #221).
  bindProductsChoices() {
    const checkboxes = Array.from(this.form.querySelectorAll('[data-products-choice]'));
    if (checkboxes.length === 0) return;
    const allCheckbox = checkboxes.find((cb) => cb.dataset.productsChoice === 'all');
    const valueInput = this.form.querySelector('[data-products-value]');

    if (allCheckbox) {
      allCheckbox.addEventListener('change', () => {
        checkboxes.forEach((cb) => {
          if (cb !== allCheckbox) cb.checked = false;
        });
      });
      checkboxes.forEach((cb) => {
        if (cb === allCheckbox) return;
        cb.addEventListener('change', () => {
          if (cb.checked) allCheckbox.checked = false;
        });
      });
    }

    // Registered after the exclusivity handlers so it reads the final state.
    const syncValue = () => {
      if (!valueInput) return;
      valueInput.value = checkboxes
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
        .join(', ');
      if (valueInput.value && this.validator) this.validator.clearError(valueInput);
    };
    checkboxes.forEach((cb) => cb.addEventListener('change', syncValue));
    syncValue();
  }

  scrollToNoticeIfPresent() {
    const notice = this.container.querySelector('.ws-form__notice');
    if (!notice) return;
    requestAnimationFrame(() => {
      if (window.lenis && typeof window.lenis.scrollTo === 'function') {
        window.lenis.scrollTo(notice, { offset: -80, duration: 1.0 });
      } else {
        notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }
}
